/** URL-driven filter bar shared by the Board and List views. */

import { useEffect, useRef, useState } from 'react'
import { useTaxonomy } from '../lib/taxonomy.jsx'
import { KINDS, PRIORITIES, STATUSES, STATUS_LABELS, activeFilterCount } from '../lib/filters.js'

function Facet({ label, axis, options, selected, onToggle }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const count = selected.length
  return (
    <div className="facet" ref={ref}>
      <button
        type="button"
        className={`btn sm${count ? ' on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {label}
        {count ? ` · ${count}` : ''}
      </button>
      {open && (
        <div className="facet-menu" role="listbox">
          {options.length === 0 && <div className="empty">nothing to filter by yet</div>}
          {options.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => onToggle(axis, option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Render the filter bar.
 *
 * @param {{filters: object, toggle: Function, setValue: Function, clear: Function,
 *          count: number, showStatus?: boolean, right?: React.ReactNode}} props
 */
export default function FilterBar({
  filters,
  toggle,
  setValue,
  clear,
  count,
  showStatus = false,
  right = null,
}) {
  const { cities, components, users } = useTaxonomy()
  const active = activeFilterCount(filters)

  return (
    <div className="filterbar">
      <input
        className="input"
        style={{ width: 220 }}
        placeholder="Search key, title, body, verify"
        value={filters.q}
        onChange={(event) => setValue('q', event.target.value)}
      />
      <Facet
        label="City"
        axis="city"
        selected={filters.city}
        onToggle={toggle}
        options={cities.map((c) => ({ value: c.slug, label: c.name }))}
      />
      <Facet
        label="Component"
        axis="component"
        selected={filters.component}
        onToggle={toggle}
        options={components.map((c) => ({ value: c.slug, label: c.name }))}
      />
      <Facet
        label="Assignee"
        axis="assignee"
        selected={filters.assignee}
        onToggle={toggle}
        options={[
          { value: 'none', label: 'Unassigned' },
          ...users.map((u) => ({ value: u.email, label: u.name || u.email })),
        ]}
      />
      <Facet
        label="Kind"
        axis="kind"
        selected={filters.kind}
        onToggle={toggle}
        options={KINDS.map((k) => ({ value: k, label: k }))}
      />
      <Facet
        label="Priority"
        axis="priority"
        selected={filters.priority}
        onToggle={toggle}
        options={PRIORITIES.map((p) => ({ value: p, label: p.toUpperCase() }))}
      />
      {showStatus && (
        <Facet
          label="Status"
          axis="status"
          selected={filters.status}
          onToggle={toggle}
          options={STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
        />
      )}
      {active > 0 && (
        <button type="button" className="btn sm ghost" onClick={clear}>
          Clear
        </button>
      )}
      <span className="spacer" />
      {right}
      <span className="count">{count} tickets</span>
    </div>
  )
}
